/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect, useCallback } from "react";

import { accessAPI, formatFecha, ArchivoPreview, Sidebar, TablaFiltrable } from "../../../shared";

import { Column } from "primereact/column";
import { Image } from "primereact/image";

import { Titulo } from "../../../shared";
import { Formulario } from "../../../shared";

import { Toast } from "primereact/toast";
import { Boton } from "../../../shared";

export default function ProductoEnActividad({ producto, pasoId, onProductosActualizados, modoLectura }) {
  const [loaderSubmit, setLoaderSubmit] = useState(false);
  const [camposEnProducto, setCamposEnProducto] = useState(
    producto.producto_x_productocampospropios
  );
  const [campoSeleccionado, setCampoSeleccionado] = useState();
  const [imagenesVerificaciones, setImagenesVerificaciones] = useState({});

  const toast = useRef(null);

  useEffect(() => {
    setCamposEnProducto(producto.producto_x_productocampospropios);
  }, [producto]);

  const cargarImagenVerificacion = useCallback(
    (verificacionId) => {
      if (imagenesVerificaciones[verificacionId]) return;
      accessAPI(
        "GET",
        `colab/actividad/comentariocampo/${verificacionId}/imagen`,
        null,
        (blob) => {
          const url = URL.createObjectURL(blob);
          setImagenesVerificaciones((prev) => ({
            ...prev,
            [verificacionId]: url,
          }));
        },
        () => {
          setImagenesVerificaciones((prev) => ({
            ...prev,
            [verificacionId]: "error",
          }));
        },
        null,
        true
      );
    },
    [imagenesVerificaciones]
  );

  const verificandoFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "comentario",
            tipoitemformularioid: 11,
            etiqueta: "Comentarios",
          },
          {
            nombre: "archivo",
            tipoitemformularioid: 10,
            etiqueta: "Archivo o imagen",
          },
        ],
      },
    ],
    botones: [
      {
        etiqueta: "Rechazar",
        callback: (contenidoCampos, archivo) => {
          aceptarCampo(contenidoCampos, archivo, false);
        },
        validar: true,
        confirmacion: "¿La característica es incorrecta en el producto?",
        rojo: true,
      },
      {
        etiqueta: "Aprobar",
        callback: (contenidoCampos, archivo) => {
          aceptarCampo(contenidoCampos, archivo, true);
        },
        validar: true,
        confirmacion: "¿Aprobar característica?",
        verde: true,
      },
    ],
  };

  function aceptarCampo(contenidoCampos, archivo, aprobado) {
    setLoaderSubmit(true);
    contenidoCampos.aprobado = aprobado;
    contenidoCampos.actividadPasoId = pasoId;
    contenidoCampos.campoPropioId = campoSeleccionado.id;
    accessAPI(
      "POST",
      "colab/actividad/aprobacioncampo",
      contenidoCampos,
      (respuesta) => {
        setCampoSeleccionado(null);
        setLoaderSubmit(false);
        if (respuesta.productos && onProductosActualizados) {
          onProductosActualizados(respuesta.productos);
        }
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Evaluación ingresada correctamente",
          life: 3000,
        });
      },
      (respuesta) => {
        setLoaderSubmit(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0].msg ||
            "Ocurrió un error, intente nuevamente más tarde",
          sticky: true,
        });
      },
      archivo
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Sidebar
        visible={campoSeleccionado}
        onHide={() => setCampoSeleccionado(null)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Campo: {campoSeleccionado?.nombre}</Titulo>
          <p>Valor: {campoSeleccionado?.valor}</p>
          {!modoLectura && (
            <Formulario config={verificandoFormConfig} loader={loaderSubmit} />
          )}

          {/* Historial de validaciones */}
          {campoSeleccionado?.verificaciones?.length > 0 && (
            <div css={{ marginTop: "1.5em" }}>
              <small css={{ color: "var(--grisTextos)", fontWeight: "600", display: "block", marginBottom: "0.5em" }}>
                <i className="pi pi-verified" css={{ marginRight: "0.5em" }}></i>
                Validaciones en esta actividad
              </small>
              <div css={{ display: "flex", flexDirection: "column", gap: "0.4em" }}>
                {[...campoSeleccionado.verificaciones].sort((a, b) => new Date(b.fecha) - new Date(a.fecha)).map((verificacion) => {
                  const nombreArchivo = verificacion.archivo?.nombredescarga || "";
                  const esImagen = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(nombreArchivo);

                  if (verificacion.archivo?.id && esImagen && !imagenesVerificaciones[verificacion.id]) {
                    cargarImagenVerificacion(verificacion.id);
                  }

                  return (
                    <div
                      key={verificacion.id}
                      css={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.5em",
                        padding: "0.4em 0.5em",
                        backgroundColor: verificacion.aprobado
                          ? "rgba(76, 175, 80, 0.15)"
                          : "rgba(244, 67, 54, 0.15)",
                        borderRadius: "4px",
                        borderLeft: `3px solid ${
                          verificacion.aprobado
                            ? "var(--verde)"
                            : "var(--rojo)"
                        }`,
                      }}
                    >
                      <i
                        className={verificacion.aprobado ? "pi pi-check" : "pi pi-times"}
                        css={{
                          color: verificacion.aprobado ? "var(--verde)" : "var(--rojo)",
                          marginTop: "0.15em",
                          fontSize: "0.85em"
                        }}
                      ></i>
                      <div css={{ flex: 1, fontSize: "0.85em" }}>
                        <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <small css={{ fontWeight: "600" }}>
                            {verificacion.auditor?.nombre} {verificacion.auditor?.apellido}
                          </small>
                          <small css={{ color: "#999" }}>
                            {formatFecha(verificacion.fecha)}
                          </small>
                        </div>
                        {verificacion.actividadPaso?.nombre && (
                          <small css={{ color: "var(--grisTextos)", display: "block" }}>
                            {verificacion.actividadPaso.nombre}
                            {verificacion.actividadPaso.pasoTipo?.nombre &&
                              ` (${verificacion.actividadPaso.pasoTipo.nombre})`}
                          </small>
                        )}
                        {verificacion.comentario && (
                          <small css={{ display: "block", marginTop: "0.15em", fontStyle: "italic" }}>
                            "{verificacion.comentario}"
                          </small>
                        )}
                        {verificacion.archivo?.id && esImagen && (
                          <div css={{ marginTop: "0.4em" }}>
                            {imagenesVerificaciones[verificacion.id] &&
                            imagenesVerificaciones[verificacion.id] !== "error" ? (
                              <Image
                                src={imagenesVerificaciones[verificacion.id]}
                                alt="Imagen de verificación"
                                width="150"
                                preview
                                imageStyle={{ borderRadius: "4px" }}
                              />
                            ) : imagenesVerificaciones[verificacion.id] === "error" ? (
                              <small css={{ color: "var(--rojo)" }}>
                                Error al cargar imagen
                              </small>
                            ) : (
                              <div
                                css={{
                                  width: "150px",
                                  height: "100px",
                                  backgroundColor: "#e0e0e0",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                <i className="pi pi-spin pi-spinner"></i>
                              </div>
                            )}
                          </div>
                        )}
                        {verificacion.archivo?.id && !esImagen && (
                          <div css={{ marginTop: "0.4em" }}>
                            <ArchivoPreview
                              nombre={nombreArchivo}
                              hash={verificacion.archivo?.hash}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Sidebar>

      <TablaFiltrable conFiltros={false}
        value={camposEnProducto}
        emptyMessage="No hay archivos cargados"
        size="small"
        style={{ marginBottom: "2em" }}
      >
        <Column
          field={(campo) =>
            campo.productocampospropios.unidades
              ? `${campo.productocampospropios.etiqueta} (${campo.productocampospropios.unidades})`
              : campo.productocampospropios.etiqueta
          }
          header="Campo"
        ></Column>
        <Column field={(campo) => `${campo.valor}`} header="Valor"></Column>
        <Column
          body={(campo) => {
            if (!campo.ComentarioCampo.length) return null;
            const ultima = [...campo.ComentarioCampo].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
            return (
              <i
                className={`pi ${ultima.aprobado ? "pi-check" : "pi-times"}`}
                css={{
                  fontWeight: "900",
                  color: ultima.aprobado ? "var(--verde)" : "var(--rojo)",
                }}
              />
            );
          }}
          style={{ width: "30px" }}
        />
        <Column
          body={(campo) => (
            <Boton
              texto
              chico
              negro
              css={{ borderRadius: "0" }}
              callback={() =>
                setCampoSeleccionado({
                  id: campo.id,
                  nombre: campo.productocampospropios.unidades
                    ? `${campo.productocampospropios.etiqueta} (${campo.productocampospropios.unidades})`
                    : campo.productocampospropios.etiqueta,
                  valor: campo.valor,
                  verificaciones: campo.ComentarioCampo,
                })
              }
            >
              <i className="pi pi-chevron-right"></i>
            </Boton>
          )}
          style={{ width: "30px" }}
        />
      </TablaFiltrable>
    </>
  );
}
